/**
 * Único punto que habla con el Worker `trama-api`.
 *
 * Responsabilidades:
 *   - Enviar la cookie de Cloudflare Access en cada request (credentials: include).
 *   - Parsear errores del Worker a `ApiError` con status + mensaje.
 *   - Detectar sesión expirada de Cloudflare Access y forzar reload (Fase 26).
 *
 * Decisión de diseño: NO mantenemos estado acá (loading, cache, etc.).
 * Eso vive en atoms de Jotai. Este módulo es funciones puras ("dame
 * data", "lanzá si falla"). El estado vive en `state/`.
 *
 * URL base siempre relativa ("/api"). En dev, el dev-server proxya
 * /api/* → trama.serendipia.co/api/* con la cookie de Access. En prod,
 * el Worker y el frontend comparten origen — el browser resuelve /api
 * directamente. No hay lógica de entorno acá.
 */

const API_BASE = "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ---------------------------------------------------------------------------
// Manejo de sesión expirada (Fase 26).
//
// Cloudflare Access protege todo `/api/*`. Cuando la cookie `CF_Authorization`
// expira, Access responde con 302 a `<team>.cloudflareaccess.com` (otro
// origen). Antes de Fase 26, ese redirect cross-origin se manifestaba como
// error opaco / network error / CORS, dependiendo del browser, y la app
// quedaba viva mostrando errores por cada click sin redirigir al usuario al
// login. La solución es:
//
//   1. Hacer fetch con `redirect: "manual"` para que el browser NO siga el
//      302 cross-origin. La response llega con `type === "opaqueredirect"`,
//      status 0, body vacío. Es la señal limpia de "Access te interceptó".
//   2. Defensivamente, tratar también `status === 401` como sesión expirada.
//      En la práctica Access intercepta antes y devuelve 302; el 401 lo
//      emite el Worker en validateAccessJwt si la cookie llega pero el JWT
//      no valida (clock skew, AUD mismatch, etc.). En ambos casos el remedio
//      es el mismo: re-autenticarse.
//   3. Disparar `window.location.reload()`. El reload pasa por Access, que
//      redirige al OTP, y al volver la cookie nueva ya está seteada. El
//      autoguardado de Fase 14 garantiza que el estado del usuario está en
//      el bucket — al volver, la app rehidrata y el usuario continúa.
//
// Guard `sessionExpiredHandled`: si varios requests fallan en paralelo
// (típico: la lista de meetings dispara N status polls a la vez), todos
// detectan sesión expirada y todos llaman a reload. El guard garantiza un
// solo reload. Después del reload, la flag se resetea sola al recargar el
// módulo.
// ---------------------------------------------------------------------------

let sessionExpiredHandled = false;

function handleSessionExpired(): never {
  if (!sessionExpiredHandled) {
    sessionExpiredHandled = true;
    // En entornos sin window (tests, SSR), simplemente lanzamos. En el
    // browser, reload dispara la re-autenticación via Access.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }
  // Aunque el reload esté en curso, lanzar evita que el caller continúe
  // ejecutando lógica con datos vacíos durante los milisegundos que tarda
  // el navegador en empezar a recargar.
  throw new ApiError(401, "Sesión expirada — recargando...");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers = new Headers(init.headers);
  if (!headers.has("content-type") && init.body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (!headers.has("accept")) headers.set("accept", "application/json");

  const res = await fetch(url, {
    ...init,
    headers,
    credentials: "include", // mandar cookie de Access
    redirect: "manual", // detectar 302 de Access cuando la cookie expiró
  });

  // Sesión expirada via redirect de Access (Fase 26).
  if (res.type === "opaqueredirect") {
    handleSessionExpired();
  }

  // Sesión expirada via 401 explícito del Worker (Fase 26, defensivo).
  if (res.status === 401) {
    handleSessionExpired();
  }

  let body: unknown = null;
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      body = await res.json();
    } catch {
      // body inválido, lo dejamos en null
    }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === "object" && "error" in body && typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : null) ?? res.statusText ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

export const apiGet = <T>(path: string): Promise<T> => request<T>(path, { method: "GET" });

export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

export const apiPut = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, {
    method: "PUT",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

/**
 * PATCH con body JSON.
 *
 * Usado por los endpoints de mutación parcial de directorio (Fase 30) y
 * glosario (Fase 31). Mismo patrón que apiPut — la única diferencia es
 * el método HTTP. Mantener helpers separados deja explícito en el call
 * site si la semántica es "reemplazar todo" (PUT) o "patch parcial"
 * (PATCH), alineado con la convención de los handlers del Worker.
 */
export const apiPatch = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, {
    method: "PATCH",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

export const apiDelete = <T>(path: string): Promise<T> => request<T>(path, { method: "DELETE" });

/**
 * POST con body text/plain (no JSON).
 *
 * Único caso conocido: POST /api/processings/:id/export, que recibe el
 * SRT etiquetado crudo. Usar `apiPost` no sirve porque hace
 * `JSON.stringify(body)` y setea content-type application/json.
 *
 * Mantenemos `request` privada y exponemos un helper paralelo en lugar
 * de inlinear `fetch` en cada caller — preserva el manejo unificado de
 * errores via `ApiError`.
 */
export const apiPostText = <T>(path: string, body: string): Promise<T> =>
  request<T>(path, {
    method: "POST",
    headers: { "content-type": "text/plain; charset=utf-8" },
    body,
  });

// ---------------------------------------------------------------------------
// Tipos compartidos con el Worker.
// Si el contrato cambia en el Worker, actualizar acá también.
// ---------------------------------------------------------------------------

export type ProcessingFileEntry = {
  name: string;
  size?: number;
  updatedAt?: string;
};

export type ProcessingSummary = {
  id: string;
  files: ProcessingFileEntry[];
};

export type CreateProcessingRequest = {
  title: string;
  filename: string;
  fileSize: number;
  language: string;
  numberOfSpeakers: number;
};

export type CreateProcessingResponse = {
  processingId: string;
  uploadParUrl: string;
  uploadObjectName: string;
  expiresAt: string;
};

export type TranscribeResponse = {
  jobId: string;
  status: "transcribing";
};

export type ProcessingStatus =
  | "uploading"
  | "transcribing"
  | "ready_to_finalize"
  | "ready"
  | "tagged"
  | "failed";

export type StatusResponse = {
  status: ProcessingStatus;
  percentComplete: number | null;
  lifecycleDetails: string | null;
  rawLifecycleState:
    | "ACCEPTED"
    | "IN_PROGRESS"
    | "SUCCEEDED"
    | "FAILED"
    | "CANCELED"
    | "CANCELING"
    | null;
};

export type FinalizeResponse = {
  status: "ready";
};

export type DownloadFileResponse = {
  downloadUrl: string;
  filename: string;
  expiresAt: string;
};

// ---------------------------------------------------------------------------
// Tipos de Fase 14 — estado del usuario en wire format.
//
// Map<number, X> no serializa a JSON nativo. En el wire usamos
// Record<number, X>; la conversión ocurre en los extremos:
//   - GET → Record→Map al rehidratar atoms.
//   - PUT → Map→Record al serializar para el body.
// ---------------------------------------------------------------------------

/** Shape de un speaker profile tal como viaja en el wire. */
export type WireSpeakerProfile = {
  name: string;
  role: string;
};

/**
 * Response de GET /api/processings/:id.
 * Solo listamos los campos que el frontend efectivamente usa.
 */
export type GetProcessingResponse = {
  schemaVersion: 1;
  id: string;
  title: string;
  status: ProcessingStatus;
  audioFilename?: string;
  version?: number;
  speakerProfiles?: Record<number, WireSpeakerProfile>;
  customSpeakers?: number[];
  manualOverrides?: Record<number, number>;
  threshold?: number;
  pipelineStatus?: string; // Fase 38 — estado del pipeline IA (opcional)
  pipelineError?: {        // Fase 39 — error del pipeline en background
    message: string;
    at: string;
    elapsedMs: number;
  };
};

/** Body del PUT /api/processings/:id/meta. */
export type SaveUserStateRequest = {
  version: number;
  speakerProfiles: Record<number, WireSpeakerProfile>;
  customSpeakers: number[];
  manualOverrides: Record<number, number>;
  threshold: number;
};

/** Response 200 del PUT. */
export type SaveUserStateResponse = {
  version: number;
  updatedAt: string;
};

/**
 * Response 409 del PUT (conflict).
 * El frontend lo usa para mostrar el diálogo "descartar / sobreescribir".
 */
export type SaveUserStateConflict = {
  conflict: true;
  currentVersion: number;
  currentState: {
    speakerProfiles?: Record<number, WireSpeakerProfile>;
    customSpeakers?: number[];
    manualOverrides?: Record<number, number>;
    threshold?: number;
  };
};

// ---------------------------------------------------------------------------
// Tipos de Fase 15 — Export del SRT etiquetado al bucket.
// ---------------------------------------------------------------------------

/**
 * Response 200 de POST /api/processings/:id/export.
 *
 * El backend persiste el SRT como `tagged.srt` en el folder del meeting
 * (sobreescribe si ya existía) y deja el meta en status "tagged".
 * Endpoint idempotente: re-exportar mantiene "tagged" y refresca updatedAt.
 */
export type ExportTaggedSrtResponse = {
  status: "tagged";
  updatedAt: string;
};

// ---------------------------------------------------------------------------
// Tipos de Fase 30 — Directorio compartido del tenant.
//
// Espejo del modelo del Worker (ver `04-backend-state.md`). El archivo
// vive en `_shared/directory.json` con optimistic locking via etag de OCI.
// El frontend recibe `version` en cada GET pero NO la envía en las
// mutaciones — el locking es server-side. Si dos tabs colisionan en un
// PATCH/POST/DELETE el backend devuelve 409 con `currentDirectory`,
// que el frontend usa para rehidratar su copia local sin re-fetchear.
//
// El `addedAt` y el `id` (UUIDv7) los asigna el server. `lastSeenAt` lo
// actualiza el pipeline IA (Fase 63), no la UI de admin.
// ---------------------------------------------------------------------------

/** Una persona en el directorio del tenant. */
export type DirectoryPerson = {
  id: string;
  names: string[];          // 1..N variantes; el primero es el canónico
  cargo?: string;
  empresa?: string;
  email?: string;
  addedAt: string;          // ISO 8601, asignado por el server
  lastSeenAt?: string;      // ISO 8601, lo actualiza el pipeline (Fase 63)
};

/** Response de GET /api/directory (eager init garantizada, nunca 404). */
export type Directory = {
  schemaVersion: 1;
  version: number;
  people: DirectoryPerson[];
};

/**
 * Body de POST /api/directory/people.
 * `names` es obligatorio y no admite array vacío ni strings vacíos.
 */
export type AddDirectoryPersonRequest = {
  names: string[];
  cargo?: string;
  empresa?: string;
  email?: string;
};

/**
 * Body de PATCH /api/directory/people/:id.
 *
 * Convención del patch (importante para distinguir "no tocar" vs "borrar"):
 *   - Campo ausente → no se toca.
 *   - Campo presente con valor → se sobreescribe (con trim).
 *   - Campo presente con string vacío "" → se borra (queda undefined).
 *
 * `names` no admite borrado parcial: si viene, debe ser array no vacío de
 * strings no vacíos. Mandar `names: []` o `names: [""]` devuelve 400.
 */
export type UpdateDirectoryPersonRequest = {
  names?: string[];
  cargo?: string;
  empresa?: string;
  email?: string;
};

/** Response de DELETE /api/directory/people/:id (idempotente). */
export type DeleteDirectoryPersonResponse = {
  deleted: true;
  id: string;
};

/**
 * Response 409 de cualquier mutación de directorio.
 *
 * El backend hace retry-once internamente. Cuando aún no logra el lock,
 * carga el estado más fresco posible y lo devuelve en `currentDirectory`
 * para que el caller pueda actualizar su copia local sin hacer un GET extra.
 */
export type DirectoryConflictResponse = {
  conflict: true;
  currentVersion: number;
  currentDirectory: Directory;
};

// ---------------------------------------------------------------------------
// Tipos de Fase 31 — Glosario técnico compartido del tenant.
//
// Análogo al directorio. Archivo en `_shared/glossary.json` con mismo
// optimistic locking. El frontend en v1 solo expone `category="global"`
// (decisión del roadmap de Fase 31); el shape mantiene el campo abierto
// para multi-tenant en Fase 64+.
//
// Regla cross-field del backend: `category="client"` requiere `clientId`.
// El frontend la respeta enviando siempre `category: "global"` y sin
// `clientId` — así nunca dispara el 400 por estado inconsistente.
// ---------------------------------------------------------------------------

/** Un término del glosario del tenant. */
export type GlossaryTerm = {
  id: string;
  term: string;             // forma canónica, ej. "Kubernetes"
  variants: string[];       // 0..N variantes ASR observadas (puede ser vacío)
  category: "global" | "client";
  clientId?: string;        // requerido sii category === "client"
  notes?: string;           // ≤ 500 chars, no lo lee la IA
  addedAt: string;          // ISO 8601, asignado por el server
};

/** Response de GET /api/glossary (eager init garantizada, nunca 404). */
export type Glossary = {
  schemaVersion: 1;
  version: number;
  terms: GlossaryTerm[];
};

/**
 * Body de POST /api/glossary/terms.
 *
 * `term` es obligatorio y no admite vacío. `variants` es obligatorio
 * pero puede ser array vacío (diferencia con `names` del directorio).
 * `category` siempre debe estar presente.
 */
export type AddGlossaryTermRequest = {
  term: string;
  variants: string[];
  category: "global" | "client";
  clientId?: string;
  notes?: string;
};

/**
 * Body de PATCH /api/glossary/terms/:id.
 *
 * Convención del patch (alineada con el directorio, con dos diferencias):
 *   - `term` no admite borrado: si viene, debe ser string no vacío.
 *     Mandar `term: ""` devuelve 400.
 *   - `variants` si viene reemplaza wholesale (puede ser []).
 *   - `clientId` y `notes` con string vacío "" → borran.
 *
 * Regla cross-field: el resultado mergeado debe cumplir
 * `category="client" ⇄ clientId definido`. Un patch tipo `{clientId: ""}`
 * sobre un término client deja `category="client"` sin clientId → 400.
 * El frontend en v1 evita estos casos porque solo expone "global".
 */
export type UpdateGlossaryTermRequest = {
  term?: string;
  variants?: string[];
  category?: "global" | "client";
  clientId?: string;
  notes?: string;
};

/** Response de DELETE /api/glossary/terms/:id (idempotente). */
export type DeleteGlossaryTermResponse = {
  deleted: true;
  id: string;
};

/**
 * Response 409 de cualquier mutación de glosario.
 *
 * Mismo patrón que `DirectoryConflictResponse`: el backend ya hizo el
 * retry interno y devuelve el estado más fresco posible en
 * `currentGlossary` para que el frontend rehidrate sin GET extra.
 */
export type GlossaryConflictResponse = {
  conflict: true;
  currentVersion: number;
  currentGlossary: Glossary;
};

// ---------------------------------------------------------------------------
// Endpoints tipados.
// ---------------------------------------------------------------------------

export const listProcessings = (): Promise<{ processings: ProcessingSummary[] }> =>
  apiGet("/processings");

export const cancelTranscription = (id: string): Promise<void> =>
  apiDelete(`/processings/${encodeURIComponent(id)}/transcribe`);

export const deleteProcessing = (id: string): Promise<void> =>
  apiDelete(`/processings/${encodeURIComponent(id)}`);

export const createProcessing = (
  body: CreateProcessingRequest
): Promise<CreateProcessingResponse> => apiPost("/processings", body);

export const transcribeProcessing = (id: string): Promise<TranscribeResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/transcribe`);

export const getProcessingStatus = (id: string): Promise<StatusResponse> =>
  apiGet(`/processings/${encodeURIComponent(id)}/status`);

export const finalizeProcessing = (id: string): Promise<FinalizeResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/finalize`);

export const getProcessingFile = (
  id: string,
  filename: string
): Promise<DownloadFileResponse> =>
  apiGet(`/processings/${encodeURIComponent(id)}/files/${encodeURIComponent(filename)}`);

/**
 * GET /api/processings/:id — lee el meta.json completo.
 * Usado al abrir un meeting existente para rehidratar el estado del usuario.
 */
export const getProcessing = (id: string): Promise<GetProcessingResponse> =>
  apiGet(`/processings/${encodeURIComponent(id)}`);

/**
 * PUT /api/processings/:id/meta — autoguardado del estado del usuario.
 *
 * Lanza ApiError con status 409 si hay conflict (otro tab guardó antes).
 * El caller es responsable de manejar el 409 y decidir qué hacer.
 */
export const saveUserState = (
  id: string,
  body: SaveUserStateRequest
): Promise<SaveUserStateResponse> =>
  apiPut(`/processings/${encodeURIComponent(id)}/meta`, body);

/**
 * POST /api/processings/:id/export — guarda el SRT etiquetado en el bucket.
 *
 * El body es text/plain con el SRT crudo (no JSON). El backend lo persiste
 * como `tagged.srt` (sobreescribe si ya existía) y deja el meta en status
 * "tagged". Idempotente.
 *
 * Status del meta requerido: "ready" o "tagged". Si está en otro estado,
 * el backend devuelve 409 (ApiError con status === 409).
 *
 * No bloquea descarga local: el caller suele hacer `downloadTextAsFile`
 * primero y llamar a esto en paralelo / después. Si esto falla, el archivo
 * local ya está en el disco del usuario.
 */
export const exportTaggedSrt = (
  id: string,
  srt: string
): Promise<ExportTaggedSrtResponse> =>
  apiPostText(`/processings/${encodeURIComponent(id)}/export`, srt);

// ---------------------------------------------------------------------------
// Directorio (Fase 30) — CRUD del directorio compartido del tenant.
//
// Todas las mutaciones pueden tirar `ApiError` con status 409 si hay
// conflict. El `body` del ApiError es `DirectoryConflictResponse`; el
// caller puede leerlo via `(err.body as DirectoryConflictResponse).currentDirectory`
// para refrescar su copia local sin un GET extra.
//
// El backend devuelve solo la entidad afectada (no el directorio completo),
// así que el frontend tiene dos estrategias razonables al éxito:
//   - Insertar/reemplazar/quitar el item en la lista local y bumpear su
//     copia local del `version` en +1 (estimación optimista).
//   - O hacer fetchDirectory() de vuelta. Más simple, menos performante.
// La pantalla de admin (Fase 32) usa la primera con fallback a refetch
// si algo falla.
// ---------------------------------------------------------------------------

export const fetchDirectory = (): Promise<Directory> => apiGet("/directory");

export const addDirectoryPerson = (
  body: AddDirectoryPersonRequest
): Promise<DirectoryPerson> => apiPost("/directory/people", body);

export const updateDirectoryPerson = (
  id: string,
  body: UpdateDirectoryPersonRequest
): Promise<DirectoryPerson> =>
  apiPatch(`/directory/people/${encodeURIComponent(id)}`, body);

export const deleteDirectoryPerson = (
  id: string
): Promise<DeleteDirectoryPersonResponse> =>
  apiDelete(`/directory/people/${encodeURIComponent(id)}`);

// ---------------------------------------------------------------------------
// Glosario (Fase 31) — CRUD del glosario técnico compartido del tenant.
//
// Mismo patrón que el directorio. Las mutaciones tiran ApiError 409 con
// `body` = `GlossaryConflictResponse` cuando hay conflict; `currentGlossary`
// trae el estado fresco para rehidratar sin GET extra.
//
// En v1 el frontend siempre envía `category: "global"` (sin `clientId`),
// por lo que la regla cross-field del backend nunca dispara 400 desde
// la UI de admin. Si en algún momento se expone "client", revisar
// `UpdateGlossaryTermRequest` para evitar dejar términos en estado
// inconsistente con patches parciales.
// ---------------------------------------------------------------------------

export const fetchGlossary = (): Promise<Glossary> => apiGet("/glossary");

export const addGlossaryTerm = (
  body: AddGlossaryTermRequest
): Promise<GlossaryTerm> => apiPost("/glossary/terms", body);

export const updateGlossaryTerm = (
  id: string,
  body: UpdateGlossaryTermRequest
): Promise<GlossaryTerm> =>
  apiPatch(`/glossary/terms/${encodeURIComponent(id)}`, body);

export const deleteGlossaryTerm = (
  id: string
): Promise<DeleteGlossaryTermResponse> =>
  apiDelete(`/glossary/terms/${encodeURIComponent(id)}`);

// ---------------------------------------------------------------------------
// Upload directo a una PAR de OCI con progress events.
//
// Usamos XMLHttpRequest porque `fetch` no expone progress de upload de
// manera estándar. XHR queda contenido a esta función — no contamina el
// resto del código. El upload va directo browser → OCI sin pasar por el
// Worker (la PAR es URL absoluta a `objectstorage.us-ashburn-1...`).
//
// Nota Fase 26: esta función NO necesita lógica de sesión expirada porque
// el destino es OCI directo, no `/api/*`. La auth de Access no aplica acá;
// la PAR ya está firmada por el Worker antes de llegar a este punto.
// ---------------------------------------------------------------------------

export type UploadProgress = {
  /** Bytes subidos hasta el momento. */
  loaded: number;
  /** Bytes totales del archivo. */
  total: number;
  /** Fracción 0..1. NaN si total no está disponible (raro). */
  fraction: number;
};

export type UploadHandle = {
  /** Promise que resuelve cuando el upload termina exitosamente. */
  done: Promise<void>;
  /** Cancela el upload en curso. La promise rechaza con AbortError. */
  abort: () => void;
};

/**
 * Sube un File a una PAR de upload usando XHR con progress events.
 *
 * Devuelve { done, abort }. El caller decide cuándo:
 *   - hace `await handle.done` (espera el final).
 *   - llama a `handle.abort()` (ej. al desmontar componente o si el
 *     usuario cancela manualmente).
 *
 * `onProgress` se invoca repetidamente durante la subida con el avance
 * actualizado. Se invoca al menos una vez al inicio (loaded=0) y una
 * al final (loaded=total).
 */
export function uploadToPar(
  parUrl: string,
  file: File,
  onProgress: (p: UploadProgress) => void
): UploadHandle {
  const xhr = new XMLHttpRequest();

  const done = new Promise<void>((resolve, reject) => {
    xhr.open("PUT", parUrl, true);
    // PAR de OCI: PUT directo, sin auth headers (la URL ya tiene el token).
    if (file.type) xhr.setRequestHeader("Content-Type", file.type);
    else xhr.setRequestHeader("Content-Type", "audio/wav");

    xhr.upload.addEventListener("progress", (ev) => {
      if (ev.lengthComputable) {
        onProgress({
          loaded: ev.loaded,
          total: ev.total,
          fraction: ev.total > 0 ? ev.loaded / ev.total : NaN,
        });
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Garantizar onProgress final con loaded=total para que el caller
        // pueda mostrar 100% antes de transicionar al siguiente estado.
        onProgress({ loaded: file.size, total: file.size, fraction: 1 });
        resolve();
      } else {
        reject(
          new ApiError(
            xhr.status,
            `Upload falló con status ${xhr.status}: ${xhr.responseText.slice(0, 200)}`
          )
        );
      }
    });

    xhr.addEventListener("error", () => {
      reject(new ApiError(0, "Error de red durante el upload"));
    });

    xhr.addEventListener("abort", () => {
      // DOMException AbortError-like, marcado para que el caller pueda
      // distinguir cancelación voluntaria de error real.
      const err = new Error("Upload cancelado");
      err.name = "AbortError";
      reject(err);
    });

    // Disparar progress inicial. Algunos browsers no emiten progress hasta
    // que arrancan a subir; esto le da feedback inmediato al usuario.
    onProgress({ loaded: 0, total: file.size, fraction: 0 });
    xhr.send(file);
  });

  return {
    done,
    abort: () => xhr.abort(),
  };
}

/**
 * Helper para descargar el contenido (texto) de una downloadUrl emitida
 * por GET /api/processings/:id/files/:filename. Devuelve el body como
 * string. Lanza ApiError si la PAR rechaza.
 *
 * Para JSON usamos esto + JSON.parse en el caller; para SRT, idem.
 * No usamos `apiGet` porque la URL no es del Worker — es directa a OCI.
 *
 * Nota Fase 26: igual que `uploadToPar`, esta función NO necesita lógica
 * de sesión expirada. El destino es OCI directo, no `/api/*`. Si la PAR
 * vence, OCI devuelve 404; ese caso lo maneja el caller refrescando la
 * PAR via Worker (que sí va por `request()` y aplica el handling).
 */
export async function fetchTextFromPar(downloadUrl: string): Promise<string> {
  const res = await fetch(downloadUrl, { method: "GET" });
  if (!res.ok) {
    throw new ApiError(res.status, `Descarga falló (${res.status} ${res.statusText})`);
  }
  return res.text();
}

// ---------------------------------------------------------------------------
// Pipeline IA (Fase 38) — corrección lingüística P1
// ---------------------------------------------------------------------------

/** Response del POST /api/processings/:id/pipeline/correct (202). */
export type RunPipelineCorrectResponse = {
  status: "correcting";
  message: string;
  pipelineStatus: "correcting";
  version: number;
  updatedAt: string;
};

/** Response del GET /api/processings/:id/pipeline/correction-report (200). */
export type GetCorrectionReportResponse = {
  reportVersion: 1;
  generatedAt: string;
  meetingId: string;
  meetingTitle: string;
  pipelineWallTimeMs: number;
  applied: AppliedCorrection[];
  pending: PendingCorrection[];
  speakers: SpeakerIdentification[];
  metrics: CorrectionMetrics;
};

export type CorrectionType =
  | "speaker_identification"
  | "technical_term"
  | "proper_name"
  | "language_mix"
  | "other";

export type ConfidenceLevel = "high" | "medium" | "low";

export type AppliedCorrection = {
  id: string;
  type: CorrectionType;
  before: string;
  after: string;
  reason: string;
  confidence: ConfidenceLevel;
  segmentIndex: number;
  timestamp: number;
};

export type PendingCorrection = {
  id: string;
  type: CorrectionType;
  before: string;
  suggestion: string;
  reason: string;
  confidence: ConfidenceLevel;
  segmentIndex: number;
  timestamp: number;
};

export type SpeakerIdentification = {
  srtLabel: string;
  assignedName: string | null;
  assignedRole?: string;
  directoryPersonId?: string;
  method: "directory_match" | "self_introduction" | "context_inference" | "unidentified";
  confidence: ConfidenceLevel;
  evidence?: string;
};

export type CorrectionMetrics = {
  totalApplied: number;
  totalPending: number;
  totalSpeakers: number;
  speakersIdentified: number;
  speakersUnidentified: number;
};

/** Dispara el pipeline P1 (modo polling). Devuelve 202. */
export const runPipelineCorrect = (
  id: string,
): Promise<RunPipelineCorrectResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/correct`);

/** Ejecuta un paso de P1. El frontend lo encadena hasta currentStep=null. */
export const runPipelineRun = (
  id: string,
): Promise<{
  status: string;
  currentStep?: string | null;
  nextStep?: string | null;
  stepCompleted?: string;
}> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/correct/run`);

/** Lee el reporte de correcciones de P1. */
export const getCorrectionReport = (
  id: string,
): Promise<GetCorrectionReportResponse> =>
  apiGet(`/processings/${encodeURIComponent(id)}/pipeline/correction-report`);

// ---------------------------------------------------------------------------
// Fase 42 — Confirmación de correcciones + retroalimentación
// ---------------------------------------------------------------------------

export type GlossarySuggestion = {
  term: string;
  variants: string[];
  category: "global";
};

export type ConfirmCorrectionsRequest = {
  decisions: Record<string, { kind: "accept" | "edit" | "skip"; correctedText?: string }>;
  speakerAssignments: Record<string, { personId: string; personName: string }>;
  glossarySuggestions: GlossarySuggestion[];
};

export type ConfirmCorrectionsResponse = {
  confirmedSrtPath: string;
  glossaryUpdates: number;
  directoryUpdates: number;
};

/** Envía decisiones, asignaciones de speaker y sugerencias de glosario al backend. */
export const confirmCorrections = (
  id: string,
  body: ConfirmCorrectionsRequest,
): Promise<ConfirmCorrectionsResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/confirm-corrections`, body);

// ---------------------------------------------------------------------------
// Fase 49 — Análisis estructural P3 (step-by-step)
// ---------------------------------------------------------------------------

/** Inicia el pipeline P3 (análisis estructural). Step-by-step — devuelve 202. */
export const runPipelineAnalyze = (
  id: string,
): Promise<{
  status: string;
  currentStep?: string | null;
  nextStep?: string | null;
  version: number;
  updatedAt: string;
}> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/analyze`);

/** Ejecuta un paso de P3. El frontend lo encadena hasta currentStep=null. */
export const runPipelineAnalyzeStep = (
  id: string,
): Promise<{
  status: string;
  currentStep?: string | null;
  nextStep?: string | null;
  stepCompleted?: string;
}> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/analyze/run`);

// ---------------------------------------------------------------------------
// Fase 52 — Confirmación de estructura (P4)
// ---------------------------------------------------------------------------

export type ConfirmStructureResponse = {
  status: "structured";
  structureConfirmedPath: string;
};

/**
 * POST /api/processings/:id/pipeline/confirm-structure
 *
 * Envía la estructura final (con todas las ediciones del usuario aplicadas)
 * al backend. El backend valida el StructureDoc, persiste
 * structure-confirmed.json y transiciona pipelineStatus a "structured".
 */
export const confirmStructure = (
  id: string,
  body: import("../lib/types.ts").StructureDoc,
): Promise<ConfirmStructureResponse> =>
  apiPost(
    `/processings/${encodeURIComponent(id)}/pipeline/confirm-structure`,
    body,
  );

// ---------------------------------------------------------------------------
// Fase 53 — Generación del informe IA (P5)
// ---------------------------------------------------------------------------

export type RunPipelineReportResponse = {
  status: "reported";
  reportPath: string;
  reportJsonPath: string;
  markdownLength: number;
};

/**
 * POST /api/processings/:id/pipeline/report
 *
 * Dispara la generación del informe final (P5). Toma srt-confirmed.srt +
 * structure-confirmed.json, genera el markdown con IA y persiste
 * report.md + report.json. Síncrono — puede tomar 30-60s.
 */
export const runPipelineReport = (
  id: string,
): Promise<RunPipelineReportResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/report`);

// ---------------------------------------------------------------------------
// Fase 56 — P6: Generación de borradores de email
// ---------------------------------------------------------------------------

export type RunPipelineDraftsResponse = {
  status: "drafted";
  draftsPath: string;
  draftCount: number;
};

/**
 * POST /api/processings/:id/pipeline/drafts
 *
 * Genera borradores de email para las personas con tareas que requieren
 * notificación. Agrupa por responsable y produce email-drafts.json.
 * Síncrono — una sola llamada IA para todos los destinatarios.
 */
export const runPipelineDrafts = (
  id: string,
): Promise<RunPipelineDraftsResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/drafts`);

/** PATCH /api/processings/:id/pipeline/drafts/:draftId (Fase 58). */
export const updateDraft = (
  id: string,
  draftId: string,
  patch: {
    to?: string;
    toName?: string;
    subject?: string;
    body?: string;
    excluded?: boolean;
  },
): Promise<{ draft: EmailDraft }> =>
  apiPatch(
    `/processings/${encodeURIComponent(id)}/pipeline/drafts/${encodeURIComponent(draftId)}`,
    patch,
  );

// ---------------------------------------------------------------------------
// Fase 60 — Cierre del pipeline + audit log
// ---------------------------------------------------------------------------

export type ClosePipelineResponse = {
  status: "done";
  auditPath: string;
  version: number;
};

/** POST /api/processings/:id/pipeline/close */
export const closePipeline = (
  id: string,
): Promise<ClosePipelineResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/close`);

// ---------------------------------------------------------------------------
// Fase 61 — Métricas del pipeline
// ---------------------------------------------------------------------------

export type PipelineMetricsBody = {
  timeSpent?: { p2?: number; p4?: number; p6?: number };
  counts?: {
    correctionsAccepted?: number;
    correctionsRejected?: number;
    correctionsManual?: number;
    decisionsConfirmed?: number;
    decisionsDismissed?: number;
    tasksConfirmed?: number;
    tasksAdded?: number;
    draftsDownloaded?: number;
  };
};

export type SubmitMetricsResponse = {
  pipelineMetrics: Record<string, unknown>;
  version: number;
};

/** POST /api/processings/:id/pipeline/metrics */
export const submitMetrics = (
  id: string,
  body: PipelineMetricsBody,
): Promise<SubmitMetricsResponse> =>
  apiPost(`/processings/${encodeURIComponent(id)}/pipeline/metrics`, body);

/** Borrador individual de email — espejo del tipo en el backend. */
export type EmailDraft = {
  id: string;
  to: string;
  toName: string;
  subject: string;
  body: string;
  reason: string;
  taskRefs: string[];
  attachReport: boolean;
  excluded?: boolean;
  downloadedAt?: string;
};

/** Colección de borradores — email-drafts.json. */
export type EmailDrafts = {
  schemaVersion: 1;
  generatedAt: string;
  drafts: EmailDraft[];
};

// ---------------------------------------------------------------------------
// Fase 53b — Persistencia de edits de estructura (autoguardado P4)
// ---------------------------------------------------------------------------

export type StructureEditsBody = {
  version: number;
  sections: Record<string, { title?: string; isRelevant?: boolean }>;
  decisions: Record<string, { kind?: "confirm" | "dismiss"; text?: string }>;
  tasks: Record<string, {
    kind?: "confirm" | "dismiss";
    text?: string;
    responsibleName?: string;
    deadline?: string;
    requiresNotification?: boolean;
  }>;
};

export type SaveStructureEditsResponse = {
  saved: true;
  version: number;
};

export const fetchStructureEdits = (
  id: string,
): Promise<StructureEditsBody> =>
  apiGet(`/processings/${encodeURIComponent(id)}/structure-edits`);

export const saveStructureEdits = (
  id: string,
  body: StructureEditsBody,
): Promise<SaveStructureEditsResponse> =>
  apiPut(`/processings/${encodeURIComponent(id)}/structure-edits`, body);
